import { useEffect, useState, useCallback } from 'react'
import { lumi } from '@/lib/lumi'
import {Save, Loader2, Plus, Trash2, X} from 'lucide-react'
import toast from 'react-hot-toast'
import type { SiteSetting } from '@/types'
import { useAuth } from '@/hooks/useAuth'

const SOCIAL_PLATFORMS = [
  { key: 'facebook', label: 'Facebook' },
  { key: 'instagram', label: 'Instagram' },
  { key: 'twitter', label: 'Twitter / X' },
  { key: 'youtube', label: 'YouTube' },
  { key: 'tiktok', label: 'TikTok' },
  { key: 'linkedin', label: 'LinkedIn' },
  { key: 'whatsapp', label: 'WhatsApp' },
]

export default function SocialSettings() {
  const { user } = useAuth()
  const [settings, setSettings] = useState<SiteSetting[]>([])
  const [loading, setLoading] = useState(true)
  const [saving, setSaving] = useState<string | null>(null)
  const [deleting, setDeleting] = useState<string | null>(null)
  const [showAdd, setShowAdd] = useState(false)
  const [newKey, setNewKey] = useState('facebook')
  const [newValue, setNewValue] = useState('')
  const [adding, setAdding] = useState(false)
  const [localValues, setLocalValues] = useState<Record<string, string>>({})

  const fetchSettings = useCallback(async () => {
    setLoading(true)
    try {
      const { list } = await lumi.entities.site_settings.list({
        filter: { category: 'social' },
        limit: 30,
      })
      const s = list as SiteSetting[]
      setSettings(s)
      const vals: Record<string, string> = {}
      s.forEach(item => { vals[item._id] = item.value })
      setLocalValues(vals)
    } catch (e) {
      console.error('Erro ao carregar redes sociais:', e)
      toast.error('Erro ao carregar configurações.')
    } finally {
      setLoading(false)
    }
  }, [])

  useEffect(() => { fetchSettings() }, [fetchSettings])

  const handleSave = async (setting: SiteSetting) => {
    const newVal = localValues[setting._id]
    if (!newVal?.trim()) {
      toast.error('A URL não pode ser vazia.')
      return
    }
    setSaving(setting._id)
    try {
      await lumi.entities.site_settings.update(setting._id, {
        value: newVal.trim(),
        updatedAt: new Date().toISOString(),
      })
      toast.success(`${setting.key} atualizado com sucesso!`)
      setSettings(prev => prev.map(s => s._id === setting._id ? { ...s, value: newVal.trim() } : s))
    } catch {
      toast.error('Erro ao salvar.')
    } finally {
      setSaving(null)
    }
  }

  const handleDelete = async (setting: SiteSetting) => {
    setDeleting(setting._id)
    try {
      await lumi.entities.site_settings.delete(setting._id)
      toast.success(`${setting.key} removido.`)
      setSettings(prev => prev.filter(s => s._id !== setting._id))
    } catch {
      toast.error('Erro ao remover.')
    } finally {
      setDeleting(null)
    }
  }

  const handleAdd = async (e: React.FormEvent) => {
    e.preventDefault()
    if (!newValue.trim()) {
      toast.error('Informe a URL da rede social.')
      return
    }
    setAdding(true)
    try {
      const now = new Date().toISOString()
      await lumi.entities.site_settings.create({
        key: newKey,
        value: newValue.trim(),
        category: 'social',
        creator: user?.userId || '',
        createdAt: now,
        updatedAt: now,
      })
      toast.success(`${newKey} adicionado com sucesso!`)
      setNewValue('')
      setShowAdd(false)
      fetchSettings()
    } catch {
      toast.error('Erro ao adicionar rede social.')
    } finally {
      setAdding(false)
    }
  }

  const getPlatformLabel = (key: string) => {
    return SOCIAL_PLATFORMS.find(p => p.key === key)?.label || key
  }

  return (
    <div className="p-6 max-w-3xl mx-auto">
      <div className="mb-6 flex items-center justify-between">
        <div>
          <h1 className="text-2xl font-bold text-gray-900 dark:text-white">Redes Sociais</h1>
          <p className="text-sm text-gray-500 dark:text-gray-400 mt-1">Ícones exibidos no cabeçalho do site</p>
        </div>
        <button
          onClick={() => setShowAdd(true)}
          className="flex items-center gap-2 px-4 py-2 bg-red-600 hover:bg-red-700 text-white text-sm font-semibold rounded-lg transition-colors"
        >
          <Plus size={16} />
          Adicionar
        </button>
      </div>

      {loading ? (
        <div className="flex justify-center py-16"><Loader2 size={24} className="animate-spin text-red-600" /></div>
      ) : settings.length === 0 ? (
        <div className="bg-white dark:bg-gray-900 rounded-xl border border-gray-100 dark:border-gray-800 py-12 text-center text-sm text-gray-400">
          Nenhuma rede social cadastrada. Clique em "Adicionar" para começar.
        </div>
      ) : (
        <div className="space-y-3">
          {settings.map(setting => (
            <div key={setting._id} className="bg-white dark:bg-gray-900 rounded-xl border border-gray-100 dark:border-gray-800 p-4">
              <div className="flex items-center gap-3 mb-3">
                <span className="text-sm font-semibold text-gray-900 dark:text-white capitalize">
                  {getPlatformLabel(setting.key)}
                </span>
              </div>
              <div className="flex gap-2">
                <input
                  type="url"
                  value={localValues[setting._id] ?? setting.value}
                  onChange={e => setLocalValues(prev => ({ ...prev, [setting._id]: e.target.value }))}
                  placeholder="https://..."
                  className="flex-1 px-3.5 py-2.5 rounded-lg border border-gray-200 dark:border-gray-700 bg-white dark:bg-gray-800 text-gray-900 dark:text-gray-100 text-sm focus:outline-none focus:ring-2 focus:ring-red-500 transition-colors"
                />
                <button
                  onClick={() => handleSave(setting)}
                  disabled={saving === setting._id}
                  className="px-3.5 py-2.5 bg-gray-900 dark:bg-gray-700 text-white text-sm font-semibold rounded-lg hover:bg-gray-800 disabled:opacity-50 transition-colors flex items-center gap-1.5"
                >
                  {saving === setting._id ? <Loader2 size={14} className="animate-spin" /> : <Save size={14} />}
                  Salvar
                </button>
                <button
                  onClick={() => handleDelete(setting)}
                  disabled={deleting === setting._id}
                  className="p-2.5 rounded-lg text-gray-400 hover:text-red-600 hover:bg-red-50 dark:hover:bg-red-950 transition-all"
                  aria-label="Remover"
                >
                  {deleting === setting._id ? <Loader2 size={15} className="animate-spin" /> : <Trash2 size={15} />}
                </button>
              </div>
            </div>
          ))}
        </div>
      )}

      {/* Add Modal */}
      {showAdd && (
        <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50 px-4">
          <div className="bg-white dark:bg-gray-900 rounded-2xl shadow-xl border border-gray-100 dark:border-gray-800 p-6 max-w-sm w-full">
            <div className="flex items-center justify-between mb-5">
              <h3 className="text-lg font-bold text-gray-900 dark:text-white">Adicionar Rede Social</h3>
              <button onClick={() => setShowAdd(false)} className="p-1.5 rounded-lg text-gray-400 hover:text-gray-600 hover:bg-gray-100 dark:hover:bg-gray-800 transition-all">
                <X size={18} />
              </button>
            </div>
            <form onSubmit={handleAdd} className="space-y-4">
              <div>
                <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1.5">Plataforma</label>
                <select
                  value={newKey}
                  onChange={e => setNewKey(e.target.value)}
                  className="w-full px-3.5 py-2.5 rounded-lg border border-gray-200 dark:border-gray-700 bg-white dark:bg-gray-800 text-gray-900 dark:text-gray-100 text-sm focus:outline-none focus:ring-2 focus:ring-red-500 transition-colors"
                >
                  {SOCIAL_PLATFORMS.map(p => (
                    <option key={p.key} value={p.key}>{p.label}</option>
                  ))}
                </select>
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1.5">URL do Perfil</label>
                <input
                  type="url"
                  value={newValue}
                  onChange={e => setNewValue(e.target.value)}
                  placeholder="https://facebook.com/seuperfil"
                  required
                  className="w-full px-3.5 py-2.5 rounded-lg border border-gray-200 dark:border-gray-700 bg-white dark:bg-gray-800 text-gray-900 dark:text-gray-100 text-sm focus:outline-none focus:ring-2 focus:ring-red-500 transition-colors"
                />
              </div>
              <div className="flex gap-3 pt-1">
                <button type="button" onClick={() => setShowAdd(false)} className="flex-1 py-2.5 border border-gray-200 dark:border-gray-700 text-gray-700 dark:text-gray-300 text-sm font-medium rounded-lg hover:bg-gray-50 dark:hover:bg-gray-800 transition-colors">
                  Cancelar
                </button>
                <button type="submit" disabled={adding} className="flex-1 py-2.5 bg-red-600 hover:bg-red-700 text-white text-sm font-semibold rounded-lg transition-colors disabled:opacity-50 flex items-center justify-center gap-2">
                  {adding ? <Loader2 size={15} className="animate-spin" /> : null}
                  Adicionar
                </button>
              </div>
            </form>
          </div>
        </div>
      )}
    </div>
  )
}
